// Palette engine: three seed colours (paper, ink, tint) expand into the full ink/paper scales as CSS variables.
export const PRESETS = [
  { id: 'linen', name: 'Linen & Slate', hint: 'Off-white paper, bluish-grey ink (default)', paper: '#F5F5F1', ink: '#3B4759', tint: '#5B8DEF' },
  { id: 'mint', name: 'Mint & Pine', hint: 'Pale mint paper, deep teal ink', paper: '#EEF6F1', ink: '#2F5148', tint: '#3FB489' },
  { id: 'blush', name: 'Blush & Plum', hint: 'Rosy paper, plum-grey ink', paper: '#F9EFF1', ink: '#5A3E4C', tint: '#D97A9C' },
  { id: 'lavender', name: 'Lavender & Indigo', hint: 'Lilac paper, indigo-grey ink', paper: '#F2F0FA', ink: '#3F3D66', tint: '#7C6BE8' },
  { id: 'sand', name: 'Sand & Cocoa', hint: 'Warm sand paper, cocoa ink', paper: '#F8F3EA', ink: '#5A4636', tint: '#D89A4E' },
  { id: 'sky', name: 'Sky & Navy', hint: 'Pale blue paper, navy ink', paper: '#EEF4FA', ink: '#2E4460', tint: '#4C9BE8' },
  { id: 'dusk', name: 'Dusk', hint: 'Deep indigo paper, pale lilac ink', paper: '#1E1B2E', ink: '#D9D4EC', tint: '#9F8CF6', dark: true },
  { id: 'night', name: 'Night', hint: 'Charcoal paper, warm off-white ink', paper: '#1B1D22', ink: '#E7E2D8', tint: '#5B8DEF', dark: true },
];

const INK_L = { 100: 90, 200: 81, 300: 68, 400: 55, 500: 45, 600: 36, 700: null, 800: 22, 900: 16 };
const INK_L_DARK = { 100: -62, 200: -52, 300: -40, 400: -28, 500: -18, 600: -9, 700: 0, 800: 5, 900: 9 };

function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16); const r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b); const l = (max + min) / 2; let h = 0, s = 0;
  if (max !== min) { const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min); h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4; h *= 60; }
  return { h, s: s * 100, l: l * 100 };
}
function hslToRgb({ h, s, l }) {
  s /= 100; l /= 100; const k = (n) => (n + h / 30) % 12; const a = s * Math.min(l, 1 - l);
  const f = (n) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  return [f(0), f(8), f(4)];
}
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const trip = (hsl) => hslToRgb(hsl).join(' ');
const hslStr = (hsl) => `${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%`;

export function isHex(v) { return /^#[0-9a-f]{6}$/i.test((v || '').trim()); }

function buildDarkVars(paper, ink, tint) {
  const pl = clamp(paper.l, 7, 22); const il = clamp(ink.l, 78, 94);
  const line = { h: paper.h, s: Math.min(paper.s, 20), l: pl + 11 };
  const vars = {
    '--paper-50': trip({ ...paper, l: pl + 3.5 }), '--paper-100': trip({ ...paper, l: pl }), '--paper-200': trip({ ...paper, l: pl + 7 }),
    '--line': trip(line), '--tint': trip(tint), '--card-rgb': trip({ ...paper, l: pl + 4.5 }),
    '--background': hslStr({ ...paper, l: pl }), '--foreground': hslStr({ ...ink, l: il }), '--card': hslStr({ ...paper, l: pl + 4.5 }), '--card-foreground': hslStr({ ...ink, l: il }), '--popover': hslStr({ ...paper, l: pl + 5 }), '--popover-foreground': hslStr({ ...ink, l: il }),
    '--primary': hslStr({ ...ink, l: il }), '--primary-foreground': hslStr({ ...paper, l: pl }), '--secondary': hslStr({ ...paper, l: pl + 6 }), '--secondary-foreground': hslStr({ ...ink, l: il }),
    '--muted': hslStr({ ...paper, l: pl + 6 }), '--muted-foreground': hslStr({ ...ink, l: il - 30 }), '--accent': hslStr({ ...paper, l: pl + 8 }), '--accent-foreground': hslStr({ ...ink, l: il }),
    '--border': hslStr(line), '--input': hslStr({ ...line, l: line.l + 3 }), '--ring': hslStr(tint),
  };
  Object.entries(INK_L_DARK).forEach(([k, d]) => { vars[`--ink-${k}`] = trip({ ...ink, s: d < -30 ? ink.s * 0.6 : ink.s, l: clamp(il + d, 4, 98) }); });
  return vars;
}

export function buildVars(p) {
  const paper = hexToHsl(p.paper) || hexToHsl(PRESETS[0].paper);
  const ink = hexToHsl(p.ink) || hexToHsl(PRESETS[0].ink);
  const tint = hexToHsl(p.tint) || hexToHsl(PRESETS[0].tint);
  if (p.dark) return buildDarkVars(paper, ink, tint);
  const pl = clamp(paper.l, 88, 98);
  const vars = {
    '--paper-50': trip({ ...paper, l: clamp(pl + 2.5, 0, 99) }), '--paper-100': trip({ ...paper, l: pl }), '--paper-200': trip({ ...paper, l: pl - 3.5 }),
    '--line': trip({ h: paper.h, s: Math.min(paper.s, 14), l: pl - 6 }), '--tint': trip(tint),
    '--background': hslStr({ ...paper, l: pl }), '--foreground': hslStr(ink), '--card-foreground': hslStr(ink), '--popover-foreground': hslStr(ink),
    '--primary': hslStr(ink), '--primary-foreground': hslStr({ ...paper, l: 98 }), '--secondary': hslStr({ ...paper, l: pl }), '--secondary-foreground': hslStr(ink),
    '--muted': hslStr({ ...paper, l: pl }), '--muted-foreground': hslStr({ ...ink, l: 54 }), '--accent': hslStr({ ...paper, l: pl - 1 }), '--accent-foreground': hslStr({ ...ink, l: 22 }),
    '--border': hslStr({ h: paper.h, s: Math.min(paper.s, 14), l: pl - 6 }), '--input': hslStr({ h: paper.h, s: Math.min(paper.s, 14), l: pl - 8 }), '--ring': hslStr(tint),
    '--card': '0 0% 100%', '--popover': '0 0% 100%', '--card-rgb': '255 255 255',
  };
  Object.entries(INK_L).forEach(([k, l]) => { vars[`--ink-${k}`] = trip(l === null ? ink : { ...ink, s: l > 60 ? ink.s * 0.7 : ink.s, l }); });
  return vars;
}

export function applyPalette(p) {
  const root = document.documentElement;
  Object.entries(buildVars(p)).forEach(([k, v]) => root.style.setProperty(k, v));
  root.classList.toggle('dark-palette', !!p.dark);
  root.style.colorScheme = p.dark ? 'dark' : 'light';
  try { localStorage.setItem('kyc_palette', JSON.stringify({ paper: p.paper, ink: p.ink, tint: p.tint, dark: !!p.dark })); } catch { /* ignore */ }
}

export function applyStoredPalette() {
  try { const p = JSON.parse(localStorage.getItem('kyc_palette') || 'null'); if (p) applyPalette(p); } catch { /* ignore */ }
}
